import { defineComponent as _defineComponent } from 'vue'
import BCXSidebarHelpContainer from '@/components/molecules/BCXSidebarHelpContainer.vue';
import BCXSearchSelect from '@/components/molecules/BCXSearchSelect.vue';
import useUser from '@/mixins/useUser';
import {
  computed, ref, watch, Ref
} from 'vue';
import BCXTextArea from '@/components/molecules/forms/BCXTextArea.vue';
import { i18nToItemList } from '@/utils/selectItemTools';
import BCXCheckbox from '@/components/molecules/forms/BCXCheckbox.vue';
import { useStore } from 'vue2-helpers/vuex';
import { Project } from '@/views/project-forum/models/ProjectForum';
import ProjectActionApplyProfileCheck from '@/views/project-forum/components/sidebar-actions/ProjectActionApplyProfileCheck.vue';
import ProjectActionApplyError from '@/views/project-forum/components/sidebar-actions/ProjectActionApplyError.vue';
import { ProjectOfferRequest } from '@/models/MyProjects';
import { getHasOffers, postOffer } from '@/services/OffersService';
import { useAsyncState, whenever } from '@vueuse/core';
import { createVuexHelpers } from 'vue2-helpers';
import asyncWait from '@/utils/asyncWait';
import { useRouter } from 'vue2-helpers/vue-router';
import BCXDatepickerField from '@/components/molecules/forms/datepicker/BCXDatepickerField.vue';
import { useFormValidation } from '@/utils/bcxFormValidations';
import useInfoPopupState, { InfoReadRequest } from '@/state/infoPopupState';
import ProjectActionApplyMaxOffersReached from './ProjectActionApplyMaxOffersReached.vue';
import ProjectActionApplyOfferRules from './ProjectActionApplyOfferRules.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProjectActionApply',
  setup(__props) {

const { tUserFallback: t } = useUser('self');

const store = useStore();
const router = useRouter();

const project = computed<Project>(() => store.state.projectForum.project);

const billingOptions = i18nToItemList('project-forum_USERTYPE.apply-box.billing-options', t);
const remoteOptions = i18nToItemList('project-forum_USERTYPE.apply-box.remote-options', t);

const isSent = ref(false);
const isSending = ref(false);
const isMessengerEnlarged = ref(false);
const hasBeenTriedWithoutMessage = ref(false);
const errorCode = ref('');

const getISODateFromDate = (date:Date, days = 0) => {
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

const initialStartDate = (() => ((new Date(project.value.startDate).getTime() < new Date().getTime()) ? getISODateFromDate(new Date()) : project.value.startDate))();
const startDate = ref(initialStartDate);
const hasStartDateChanged = computed(() => initialStartDate !== startDate.value);

const billingAmount = ref(`${project.value.hourlyRateInEUR || ''}`);
const billingAmountFlash = ref(false);
const billingUnit = ref<ProjectOfferRequest['billingUnit']>('RATE_PER_HOUR');

// Mindest-Stundensatz , Mindest-Tagessatz
const billingAmountMin = computed(() => (billingUnit.value === 'RATE_PER_DAY' ? 400 : 50));

const enforceBillingAmountMin = () => {
  if (billingAmount.value !== '' && +billingAmount.value < billingAmountMin.value) {
    billingAmount.value = `${billingAmountMin.value}`;
    billingAmountFlash.value = true;
    setTimeout(() => {
      billingAmountFlash.value = false;
    }, 500);
  }
};

const onBillingAmountBlur = () => {
  enforceBillingAmountMin();
};

watch(billingUnit, () => {
  enforceBillingAmountMin();
});

const initialRemote = ((p: Project): ProjectOfferRequest['remoteWork'] => {
  if (p.projectLocation === 'ONSITE_ONLY') return 'ONSITE_ONLY';
  if (p.projectLocation === 'REMOTE_ONLY') return 'REMOTE_ONLY';
  return p.remoteWork as ProjectOfferRequest['remoteWork'] || 'NEGOTIABLE';
})(project.value);
const remote = ref(initialRemote);
const hasRemoteChanged = computed(() => initialRemote !== remote.value);

const message = ref('');

const initialDependingOnComplexity = project.value.dependingOnComplexity ?? false;
const dependingOnComplexity = ref(initialDependingOnComplexity);
const hasDependingOnComplexityChanged = computed(() => initialDependingOnComplexity !== dependingOnComplexity.value);

const initialHrsPerWeek = `${project.value.hoursPerWeek}`;
const hrsPerWeek = ref(initialHrsPerWeek);
const hasHrsPerWeekChanged = computed(() => initialHrsPerWeek !== hrsPerWeek.value);

let previousHrs = hrsPerWeek.value;
watch(dependingOnComplexity, (is) => {
  if (is) previousHrs = hrsPerWeek.value;
  hrsPerWeek.value = is ? '' : previousHrs;
});

const { state: userProjectOffers, isReady } = useAsyncState(async () => getHasOffers(project.value.id ?? ''), null);
const cantApply = computed(() => isReady.value && (userProjectOffers.value?.hasOffers || !project.value.offersInfo.acceptsOffers));

const maxOffersReached = computed(() => project.value.offersInfo.remainingOffers <= 0);
const maxOffersContinue = ref(false);
const continueApplication = () => {
  maxOffersContinue.value = true;
};

const offerRulesRequest: InfoReadRequest = {
  context: 'offer_rules',
  value: '1',
  visibleForRoles: {
    FREELANCER: true,
  },
};
const offerRulesResolved = ref(false);
const showOfferRules = ref(false);
const { shouldShowInfoIfUnread, markInfoRead } = useInfoPopupState();
const canResolveOfferRules = computed(() => (
  isReady.value
  && !cantApply.value
  && (!maxOffersReached.value || maxOffersContinue.value)
));

watch(canResolveOfferRules, async (canResolve) => {
  if (!canResolve || offerRulesResolved.value) return;
  try {
    showOfferRules.value = await shouldShowInfoIfUnread(offerRulesRequest);
  } finally {
    offerRulesResolved.value = true;
  }
}, { immediate: true });

const acceptOfferRules = () => {
  showOfferRules.value = false;
  markInfoRead(offerRulesRequest);
};

const offerValidUntilDate = ref<string | undefined>(getISODateFromDate(new Date(), 30));

const offerValidDurationDays = computed(() => {
  if (!offerValidUntilDate.value) return null;
  const diff = new Date(offerValidUntilDate.value).getTime() - new Date().getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
});
const offerValidDurationDaysText = computed(() => t('project-forum.apply-box.offer-valid-until-days', { days: offerValidDurationDays.value }));

const warnAboutSmallDuration = computed(() => offerValidDurationDays.value < 8);

const earliestValidUntilDate = computed(() => getISODateFromDate(new Date(), 4));

const { validateRate, validateHrsPerWeek } = useFormValidation();
const billingAmountError: Ref<string | null> = ref(null);
const hrsPerWeekError: Ref<string | null> = ref(null);

const send = () => {
  billingAmountError.value = validateRate(billingAmount.value);
  if (billingAmountError.value) {
    return;
  }

  if (!dependingOnComplexity.value) {
    hrsPerWeekError.value = validateHrsPerWeek(hrsPerWeek.value);
    if (hrsPerWeekError.value) {
      return;
    }
  }

  if (!message.value) {
    hasBeenTriedWithoutMessage.value = true;
    return;
  }

  if (isSending.value) return;
  isSending.value = true;
  errorCode.value = '';

  const payload: ProjectOfferRequest = {
    dependingOnComplexity: dependingOnComplexity.value,
    billingAmount: +billingAmount.value,
    billingUnit: billingUnit.value,
    hoursPerWeek: +hrsPerWeek.value,
    hoursPerWeekChanged: hasHrsPerWeekChanged.value,
    remoteWork: remote.value,
    remoteWorkChanged: hasRemoteChanged.value,
    startDate: startDate.value,
    startDateChanged: hasStartDateChanged.value,
    message: message.value,
    validUntil: `${offerValidUntilDate.value}`,
  };
  postOffer(project.value?.id ?? '', payload).catch(async (err) => {
    errorCode.value = err?.response?.data?.error?.code ?? 'unknown';
    await asyncWait(2000);
  }).then(() => {
    isSent.value = true;
    isSending.value = false;
  });
};

whenever(billingAmount, () => {
  billingAmountError.value = null;
});
whenever(hrsPerWeek, () => {
  hrsPerWeekError.value = null;
});
whenever(dependingOnComplexity, () => {
  if (dependingOnComplexity.value) {
    hrsPerWeekError.value = null;
  }
});
whenever(message, () => {
  hasBeenTriedWithoutMessage.value = false;
});

const { useMutations } = createVuexHelpers();
const { setIsParticipateVisible }: any = useMutations('page', ['setIsParticipateVisible']);

const triggerBugReportOrGotoMyProjects = () => {
  if (!errorCode.value) {
    router.push('/my-projects/offers');
  } else {
    setIsParticipateVisible(true);
  }
};

return { __sfc: true,t, store, router, project, billingOptions, remoteOptions, isSent, isSending, isMessengerEnlarged, hasBeenTriedWithoutMessage, errorCode, getISODateFromDate, initialStartDate, startDate, hasStartDateChanged, billingAmount, billingAmountFlash, billingUnit, billingAmountMin, enforceBillingAmountMin, onBillingAmountBlur, initialRemote, remote, hasRemoteChanged, message, initialDependingOnComplexity, dependingOnComplexity, hasDependingOnComplexityChanged, initialHrsPerWeek, hrsPerWeek, hasHrsPerWeekChanged, previousHrs, userProjectOffers, isReady, cantApply, maxOffersReached, maxOffersContinue, continueApplication, offerRulesRequest, offerRulesResolved, showOfferRules, shouldShowInfoIfUnread, markInfoRead, canResolveOfferRules, acceptOfferRules, offerValidUntilDate, offerValidDurationDays, offerValidDurationDaysText, warnAboutSmallDuration, earliestValidUntilDate, validateRate, validateHrsPerWeek, billingAmountError, hrsPerWeekError, send, useMutations, setIsParticipateVisible, triggerBugReportOrGotoMyProjects, BCXSidebarHelpContainer, BCXSearchSelect, BCXTextArea, BCXCheckbox, ProjectActionApplyProfileCheck, ProjectActionApplyError, BCXDatepickerField, ProjectActionApplyMaxOffersReached, ProjectActionApplyOfferRules }
}

})